import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { AuthContext } from "../../../context/authcontext";
import { ProposalService, ProposalRequest } from "./proposal.service";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import ProposalForm from "./proposalForm";
import { ProposalFormDataProvider } from "./proposalFormDataContext";
import companyLogo from "../../../../src/assets/company.png";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import ListFilterChips, { getClearedListFilters } from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { useTenantChange } from "../../../hooks/useTenantChange";
import ExportComponent from "../../../components/ExportComponent";
import CollapseHeader from "../../../core/common/collapse-header";
import { getStageColor } from "../../../utils/stageColors";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import {
  getLifecycleTypeName,
  getLifecycleTypeNames,
  getLifecycleTypePluralName,
} from "../../../utils/lifecycleUtils";
import ContractForm from "../contracts/contractForm";
import { ContractFormDataProvider } from "../contracts/contractFormDataContext";
import {
  ContractService,
  ContractRequest,
} from "../contracts/contract.service";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

const spinningStyles = `
  .ti-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .proposal-grid-row {
    margin-right: -10px;
    margin-left: -10px;
  }

  .proposal-grid-row > div {
    padding-right: 10px;
    padding-left: 10px;
  }

  .hover-primary:hover {
    color: #824eeb !important;
    transition: color 0.2s ease;
  }

  @media (max-width: 575px) {
    .proposal-grid-row > div {
      width: 100%;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = spinningStyles;
  if (!document.head.querySelector("style[data-spinning]")) {
    styleElement.setAttribute("data-spinning", "true");
    document.head.appendChild(styleElement);
  }
}

interface ProposalsGridViewProps {
  proposals: any[];
  loading: boolean;
  backgroundLoading?: boolean;
  error: string | null;
  pagination: any;
  fetchProposals: (page?: number, limit?: number, filterParams?: Record<string, any>) => Promise<void>;
  refreshProposals: () => Promise<void>;
  addProposal: (proposal: any) => void;
  updateProposal: (proposal: any) => void;
  removeProposal: (proposalId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;
  clearBackgroundLoading?: () => void;
}

const ProposalsGridView = ({
  proposals,
  loading,
  backgroundLoading = false,
  error,
  pagination,
  fetchProposals,
  refreshProposals,
  addProposal,
  updateProposal,
  removeProposal,
  currentView,
  onViewSwitch,
  isInitialized,
  clearBackgroundLoading,
  ...rest
}: ProposalsGridViewProps) => {
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);

  const [selectedProposal, setSelectedProposal] = useState<any | null>(null);
  const [isAddProposalOpen, setIsAddProposalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetTrigger, setFormResetTrigger] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");

  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [createContractError, setCreateContractError] = useState<string>("");
  const [resetContractFormTrigger, setResetContractFormTrigger] = useState(0);
  const [isCreateContractOpen, setIsCreateContractOpen] = useState(false);
  const [downloadingProposals, setDownloadingProposals] = useState<{
    [key: number]: boolean;
  }>({});

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [filteredProposalsData, setFilteredProposalsData] = useState<any[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchPlaceholder: `Search ${getLifecycleTypePluralName(
        "PROPOSAL"
      ).toLowerCase()}...`,
      searchField: "subject",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      pageName: "proposal" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);

    if (
      !newFilters.searchTerm &&
      !newFilters.dateRange[0] &&
      !newFilters.dateRange[1]
    ) {
      setIsFilterActive(false);
      setFilteredProposalsData([]);
    }
  }, []);

  const handleDropdownFilterChange = useCallback(
    (dropdownFilters: Record<string, any>) => {
      const updatedFilters = { 
        ...filters, 
        ...dropdownFilters
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      setIsFilterActive(false);
      setFilteredProposalsData([]);
      fetchProposals(1, pagination.pageSize, updatedFilters);
    },
    [filters, fetchProposals, pagination.pageSize]
  );

  const applyListFilters = useCallback(
    (nextFilters: Record<string, any>) => {
      setFilters(nextFilters as FilterState);
      setCurrentPage(1);
      if (!nextFilters.searchTerm && !nextFilters.dateRange?.[0] && !nextFilters.dateRange?.[1]) {
        setIsFilterActive(false);
        setFilteredProposalsData([]);
      }
      fetchProposals(1, pagination.pageSize, nextFilters);
    },
    [fetchProposals, pagination.pageSize]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => {},
    []
  );

  const handleFiltersReset = useCallback(() => {
    setIsFilterActive(false);
    setFilteredProposalsData([]);
    setCurrentPage(1);
    setFilters({
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc",
      archivedFilter: "no",
    });
    fetchProposals(1);
  }, [fetchProposals]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchProposals(page, pagination.pageSize, filters);
  };

  const handleApiCall = useCallback(
    async (url: string) => {
      if (!selectedTenantId) {
        return;
      }

      try {
        setIsFilterLoading(true);

        const authCred = localStorage.getItem("authCred") || "";
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authCred}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.results && Array.isArray(result.results)) {
          setFilteredProposalsData(result.results);
          setIsFilterActive(true);
        } else if (result.data && Array.isArray(result.data)) {
          setFilteredProposalsData(result.data);
          setIsFilterActive(true);
        } else if (Array.isArray(result)) {
          setFilteredProposalsData(result);
          setIsFilterActive(true);
        } else {
          setFilteredProposalsData([]);
          setIsFilterActive(true);
        }
      } catch (error: any) {
        console.error("Filter API error:", error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("authCred");
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("selectedTenantId");
          localStorage.removeItem("tenant_id");

          navigate("/login");
          return;
        }

        setFilteredProposalsData([]);
        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [selectedTenantId, navigate]
  );

  const createProposalUrl = (proposal: any): string => {
    return `/proposals/${proposal.proposal_id}`;
  };

  const { filteredProposals, totalFilteredCount } = useMemo(() => {
    if (isFilterActive) {
      return {
        filteredProposals: filteredProposalsData,
        totalFilteredCount: filteredProposalsData.length,
      };
    }

    if (!Array.isArray(proposals)) {
      return { filteredProposals: [], totalFilteredCount: 0 };
    }

    return {
      filteredProposals: proposals,
      totalFilteredCount: pagination.totalCount,
    };
  }, [proposals, filteredProposalsData, isFilterActive, pagination.totalCount]);

  const paginatedProposals = useMemo(() => {
    if (isFilterActive) {
      const startIndex = (currentPage - 1) * 12;
      const endIndex = startIndex + 12;
      return filteredProposals.slice(startIndex, endIndex);
    }
    return filteredProposals;
  }, [filteredProposals, isFilterActive, currentPage]);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);

  useEffect(() => {
    const addOffcanvas = document.getElementById("add_proposal");

    const handleHidden = () => {
      setIsAddProposalOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    const createContractOffcanvas = document.getElementById(
      "offcanvas_create_contract"
    );

    const handleHidden = () => {
      setIsCreateContractOpen(false);
    };

    if (createContractOffcanvas) {
      createContractOffcanvas.addEventListener(
        "hidden.bs.offcanvas",
        handleHidden
      );
    }

    return () => {
      if (createContractOffcanvas) {
        createContractOffcanvas.removeEventListener(
          "hidden.bs.offcanvas",
          handleHidden
        );
      }
    };
  }, []);

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
          modal
        );
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const handleDelete = async (proposal: any) => {
    if (!selectedTenantId) return;

    const result = await showConfirmationAlert(
      `Delete ${getLifecycleTypeName("PROPOSAL")}`,
      `Are you sure you want to delete "${proposal.subject}"? This action cannot be undone.`,
      async () => {
        await ProposalService.deleteProposal(
          proposal.proposal_id,
          Number(selectedTenantId)
        );
      }
    );

    if (result.isConfirmed) {
      removeProposal(proposal.proposal_id);
      showSuccessAlert(
        `${getLifecycleTypeName("PROPOSAL")} "${proposal.subject}" deleted successfully!`
      );
    }
  };

  const handleCloseCreateContract = () => {
    setCreateContractError("");
    closeOffcanvas("offcanvas_create_contract");
  };

  const handleCreateContractSubmit = async (
    formData: Partial<ContractRequest>
  ) => {
    if (isCreatingContract) return;

    setIsCreatingContract(true);
    setCreateContractError("");

    try {
      const newContract = await ContractService.createContract(formData as any);
      closeOffcanvas("offcanvas_create_contract");
      setResetContractFormTrigger((prev) => prev + 1);

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("CONTRACT")} "${newContract.subject}" created successfully!`
        );
      }, 500);

      setTimeout(() => {
        navigate(`/contracts/${newContract.contract_id}`);
      }, 1000);
    } catch (error: any) {
      console.error("Error creating contract:", error);
      setCreateContractError(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      showErrorAlert(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      throw error;
    } finally {
      setIsCreatingContract(false);
    }
  };

  const getPrefilledContractData = (proposal: any) => {
    if (!proposal) return {};

    return {
      subject: proposal.subject || "",
      description:
        proposal.description ||
        `${getLifecycleTypeName("CONTRACT")} for ${proposal.subject || getLifecycleTypeName("PROPOSAL")}`,
      amount: proposal.amount || undefined,
      currency_code: proposal.currency_code || "USD",
      company_id: proposal.company?.company_id || null,
      proposal_id: proposal.proposal_id || null,
      product_n_services: proposal.product_n_services || "",
      assigned_user_id: proposal.assigned_user?.user_id || null,
      assigned_team_id: proposal.assigned_team?.team_id || null,
      start_datetime: proposal.start_datetime || "",
      expiration_datetime: proposal.expiration_datetime || "",
    };
  };

  const handleDownloadProposal = async (proposal: any) => {
    if (!proposal?.proposal_id || !selectedTenantId) return;

    const proposalId = proposal.proposal_id;

    setDownloadingProposals((prev) => ({ ...prev, [proposalId]: true }));

    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/proposal/${proposalId}/generate_pdf_link/?tenant_id=${selectedTenantId}`,
        {
          method: "GET",
          headers: headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.download_url) {
        const pdfResponse = await fetch(result.download_url);
        if (!pdfResponse.ok) {
          throw new Error("Failed to fetch PDF file");
        }

        const pdfBlob = await pdfResponse.blob();

        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = result.filename || `proposal_${proposalId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);

        showSuccessAlert(
          `${getLifecycleTypeName("PROPOSAL")} PDF downloaded successfully!`
        );
      } else {
        throw new Error(
          result.message || "Failed to generate PDF download link"
        );
      }
    } catch (error: any) {
      console.error("Error downloading proposal:", error);
      showErrorAlert(
        error.message ||
          `Failed to download ${getLifecycleTypeName("PROPOSAL").toLowerCase()} PDF`
      );
    } finally {
      setDownloadingProposals((prev) => {
        const newState = { ...prev };
        delete newState[proposalId];
        return newState;
      });
    }
  };

  const handleCreate = async (proposal: Partial<ProposalRequest>) => {
    setIsSubmitting(true);
    try {
      const newProposal = await ProposalService.createProposal(proposal);
      addProposal(newProposal);

      setSelectedProposal(null);
      setFormResetTrigger((prev) => prev + 1);
      closeOffcanvas("add_proposal");

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("PROPOSAL")} "${newProposal.subject}" created successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to create proposal:", err);
      showErrorAlert(getApiErrorMessage(err, `Failed to create ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`));
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (
    id: number,
    proposal: Partial<ProposalRequest>
  ) => {
    setIsSubmitting(true);
    try {
      const updatedProposal = await ProposalService.updateProposal(
        id,
        proposal
      );
      updateProposal(updatedProposal);
      closeOffcanvas("edit_proposal");
      setSelectedProposal(null);
      setIsEditing(false);

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("PROPOSAL")} "${updatedProposal.subject}" updated successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to update proposal:", err);
      showErrorAlert(getApiErrorMessage(err, `Failed to update ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`));
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (proposal: any) => {
    flushSync(() => {
      setSelectedProposal(proposal);
      setIsEditing(true);
    });
  };

  const handlePreview = (proposal: any) => {
    const proposalUrl = createProposalUrl(proposal);
    navigate(proposalUrl);
  };

  const handleFormSubmit = async (formData: Partial<ProposalRequest>) => {
    if (selectedProposal) {
      await handleUpdate(selectedProposal.proposal_id, formData);
    } else {
      await handleCreate(formData);
    }
  };

  const formatCurrency = (amount: number, currencyCode: string) => {
    if (!amount) return "Not Set";
    return `${currencyCode} ${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Not Set";
    return formatCustomDateTime(dateString);
  };

  const renderProposalCard = (proposal: any) => (
    <div key={proposal.proposal_id} className="col-xxl-3 col-xl-4 col-md-6">
      <div className="card border h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3">
            <div className="flex-shrink-0">
              <h5 className="mb-1">
                <Link
                  to={createProposalUrl(proposal)}
                  className="text-dark text-decoration-none hover-primary"
                >
                  {proposal.subject?.length > 20 ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-${proposal.proposal_id}`}>{proposal.subject}</Tooltip>}
                    >
                      <span>{`${proposal.subject.substring(0, 20)}...`}</span>
                    </OverlayTrigger>
                  ) : (
                    proposal.subject
                  )}
                </Link>
              </h5>
            </div>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon bg-white"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa fa-ellipsis-v" />
              </Link>
              <div className="dropdown-menu dropdown-menu-right">
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#edit_proposal"
                  onClick={() => handleEdit(proposal)}
                >
                  <i className="ti ti-edit text-blue" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(proposal);
                  }}
                >
                  <i className="ti ti-trash text-danger" /> Delete
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePreview(proposal);
                  }}
                >
                  <i className="ti ti-clipboard-copy text-green" /> View{" "}
                  {getLifecycleTypeName("PROPOSAL")}
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_create_contract"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedProposal(proposal);
                    setIsCreateContractOpen(true);
                  }}
                >
                  <i className="ti ti-file-certificate text-blue" /> Create {getLifecycleTypeName("CONTRACT")}
                </Link>
                <Link
                  className={`dropdown-item ${
                    downloadingProposals[proposal.proposal_id] ? "disabled" : ""
                  }`}
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!downloadingProposals[proposal.proposal_id]) {
                      handleDownloadProposal(proposal);
                    }
                  }}
                  style={{
                    opacity: downloadingProposals[proposal.proposal_id] ? 0.6 : 1,
                    pointerEvents: downloadingProposals[proposal.proposal_id]
                      ? "none"
                      : "auto",
                  }}
                >
                  {downloadingProposals[proposal.proposal_id] ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-file-download text-blue-light me-2"></i>
                      Download {getLifecycleTypeName("PROPOSAL")}
                    </>
                  )}
                </Link>
              </div>
            </div>
          </div>
          <div className="d-block">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <p>
                  {getLifecycleTypeName("DEAL")} :{" "}
                  {proposal.deal?.title ||
                    `No Related ${getLifecycleTypeName("DEAL")}`}
                </p>
              </div>
              <div>
                <span
                  className={`badge ${getStageColor(
                    proposal.proposal_status?.proposal_status_display_name,
                    proposal.proposal_status?.proposal_status_id,
                    proposal.proposal_status?.proposal_status_order
                  )}`}
                >
                  {proposal.proposal_status?.proposal_status_display_name ||
                    "Not Set"}
                </span>
              </div>
            </div>
            <div className="mb-3">
              <p className="d-flex align-items-center mb-2">
                <span className="avatar avatar-xs border me-2 text-dark">
                  <i className="ti ti-moneybag fs-12" />
                </span>
                Total amount :{" "}
                {formatCurrency(proposal.amount, proposal.currency_code)}
              </p>
              <p className="d-flex align-items-center mb-2">
                <span className="avatar avatar-xs border me-2 text-dark">
                  <i className="ti ti-calendar-event fs-12" />
                </span>
                Start Date : {formatDate(proposal.start_datetime)}
              </p>
              <p className="d-flex align-items-center">
                <span className="avatar avatar-xs border me-2 text-dark">
                  <i className="ti ti-calendar-stats fs-12" />
                </span>
                Expiration Date : {formatDate(proposal.expiration_datetime)}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center mt-auto">
            <Link to="#" className="avatar avatar-sm bg-white border me-2">
              <img
                src={proposal.company?.avatar_url || companyLogo}
                alt={proposal.company?.company_name || "Company"}
                className="w-100 h-100 rounded"
                style={{ objectFit: "cover" }}
                onError={(e) => {
                  e.currentTarget.src = companyLogo;
                }}
              />
            </Link>
            <div className="d-flex flex-column">
              <Link
                to={`/companies/${proposal.company?.company_id}`}
                className="text-dark text-decoration-none hover-primary"
              >
                {proposal.company?.company_name || "No Company"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      {getLifecycleTypePluralName("PROPOSAL")}
                      <span className="count-title">
                        {filters.searchTerm !== "" ||
                        filters.dateRange[0] !== null ||
                        filters.dateRange[1] !== null
                          ? totalFilteredCount
                          : pagination.totalCount}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <ListFilterChips
                        entityType="proposal"
                        filters={filters}
                        onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                        onClearAll={() => applyListFilters(getClearedListFilters())}
                        disabled={loading}
                        className="mb-0"
                      />
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <ExportComponent entityType="proposal" filters={filters} />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#add_proposal"
                          onClick={() => setIsAddProposalOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add {getLifecycleTypeName("PROPOSAL")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <TableFilters
                    config={filterConfig}
                    data={proposals}
                    onFilterChange={handleFilterChange}
                    onApiFilterChange={handleApiFilterChange}
                    onApiCall={handleApiCall}
                    onFiltersReset={handleFiltersReset}
                    searchTerm={filters.searchTerm}
                    dateRange={filters.dateRange}
                    hideClearButton
                    tenantId={selectedTenantId || undefined}
                    className="mt-4"
                  />
                  {/* Right Side Icons */}
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <LifecycleFilterDropdown
                      entityType="proposal"
                      onFilterChange={handleDropdownFilterChange}
                      currentFilters={{...filters}}
                      loading={loading}
                    />
                    {/* View Toggle Icons */}
                    <div className="view-icons ms-3">
                      <Link
                        to="#"
                        className={currentView === "list" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch("list");
                        }}
                      >
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        className={currentView === "grid" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch("grid");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="card-body">
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading{" "}
                          {getLifecycleTypePluralName("PROPOSAL").toLowerCase()}...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div style={{ position: "relative" }}>
                      {isFilterLoading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            minHeight: "200px",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <span>Filtering results...</span>
                          </div>
                        </div>
                      )}
                      {/* Proposal Grid */}
                      <div className="row proposal-grid-row">
                        {Array.isArray(paginatedProposals)
                          ? paginatedProposals.map((proposal) =>
                              renderProposalCard(proposal)
                            )
                          : []}
                      </div>

                      {/* Pagination */}
                      <TablePagination
                        currentPage={pagination.currentPage || currentPage}
                        pageSize={12}
                        totalItems={
                          isFilterActive
                            ? totalFilteredCount
                            : pagination.totalCount
                        }
                        filteredItems={totalFilteredCount}
                        isFiltered={
                          filters.searchTerm !== "" ||
                          filters.dateRange[0] !== null ||
                          filters.dateRange[1] !== null
                        }
                        onPageChange={handlePageChange}
                        onPageSizeChange={() => {}}
                        entityName={getLifecycleTypePluralName("PROPOSAL")}
                        loading={loading}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Proposal Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="add_proposal"
        style={{ width: "550px" }}
       
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Add New {getLifecycleTypeName("PROPOSAL")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isAddProposalOpen && (
            <ProposalFormDataProvider isMainApiCompleted={isInitialized} shouldLoad={isAddProposalOpen || !!selectedProposal}>
              <ProposalForm
                key={`create-new-proposal-${formResetTrigger}`}
                onSubmit={handleFormSubmit}
                onCancel={() => closeOffcanvas("add_proposal")}
                isSubmitting={isSubmitting}
                resetTrigger={formResetTrigger}
              />
            </ProposalFormDataProvider>
          )}
        </div>
      </div>

      {/* Edit Proposal Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="edit_proposal"
        style={{ width: "550px" }}
       
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Edit {getLifecycleTypeName("PROPOSAL")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {selectedProposal && (
            <ProposalFormDataProvider isMainApiCompleted={true}>
              <ProposalForm
                proposal={selectedProposal}
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
                onCancel={() => {
                  setSelectedProposal(null);
                  closeOffcanvas("edit_proposal");
                }}
                isMainApiCompleted={true}
              />
            </ProposalFormDataProvider>
          )}
        </div>
      </div>

      {/* Delete Proposal Modal Removed */}

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_create_contract"
        style={{ maxWidth: "600px" }}
       
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Create {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {createContractError && (
            <div className="alert alert-danger" role="alert">
              {createContractError}
            </div>
          )}
          {isCreateContractOpen && (
            <ContractFormDataProvider isMainApiCompleted={true}>
              <ContractForm
                key={`create-contract-${resetContractFormTrigger}`}
                onSubmit={handleCreateContractSubmit}
                onCancel={handleCloseCreateContract}
                isSubmitting={isCreatingContract}
                resetTrigger={resetContractFormTrigger}
                prefilledData={getPrefilledContractData(selectedProposal)}
              />
            </ContractFormDataProvider>
          )}
        </div>
      </div>
    </>
  );
};

export default ProposalsGridView;
